import { isInDeal } from '../actions/promoDealsActions'
import { generateCartItem } from '../reducers/CartReducer'

/**
 * Combine cart, backorder and products together
 *
 * @param products
 * @param cart
 * @param backorder
 */
export const combineCartBackOrderData = (products, cart = [], backorder = [], store) => {
  let list = {}

  /**
   * Helper function to set default to product data
   *
   * @param BuyableID
   */
  const createIfNotExist = (newList, BuyableID) => {
    if (newList[BuyableID] === undefined) {
      newList[BuyableID] = {
        ...newList[BuyableID],
        ...products[BuyableID],

        // For Backorder
        caseBackOrder: 0,
        unitBackOrder: 0,

        // For Cart
        caseQty: 0,
        unitQty: 0,

        // Cart + Backorder
        caseTotal: 0,
        unitTotal: 0,

        // This is the total of respective group (sum)
        cartTotal: 0,
        backOrderTotal: 0,

        // Count of items in cart & backorder (if unit is available, unit will be used)
        cartQty: 0,
        backOrderQty: 0,

        // For Loyalty Reward Points
        //rewardPoints: 0,
      }
    }

    return newList
  }

  // Cart
  // -----------------
  Object.entries(cart).forEach(([dataKey, item]) => {
    const itemCase = item.case || false
    const itemUnit = item.unit || false

    const BuyableID = itemCase ? itemCase.BuyableID : itemUnit.BuyableID

    if (products[BuyableID] !== undefined) {
      // Set default if doesn't exist
      list = createIfNotExist(list, BuyableID)

      // Add cart data
      const { qty_per_case, sell_by_case, sku } = list[BuyableID]

      if (itemCase) {
        list[BuyableID]._cartItemID_Case = item.ID
        list[BuyableID].case_price = itemCase.UnitPrice
        list[BuyableID].caseTotal += itemCase.TotalUnitItems
        list[BuyableID].caseQty += itemCase.TotalUnitItems
        list[BuyableID].cartQty += sell_by_case ? itemCase.TotalUnitItems : itemCase.TotalUnitItems * qty_per_case
        list[BuyableID].cartTotal += itemCase.CalculatedSubTotalPrice
        list[BuyableID].isDeal = isInDeal(list[BuyableID]?.sku, store)
        //list[BuyableID].cartTotal += itemCase.TotalPrice
        //list[BuyableID].rewardPoints += Number(itemCase.PointsEarned) || 0;
      }

      if (itemUnit) {
        list[BuyableID]._cartItemID_Unit = item.ID
        list[BuyableID].unit_price = itemUnit.UnitPrice
        list[BuyableID].unitTotal += itemUnit.TotalUnitItems
        list[BuyableID].unitQty += itemUnit.TotalUnitItems
        list[BuyableID].cartQty += itemUnit.TotalUnitItems
        list[BuyableID].cartTotal += itemUnit.CalculatedSubTotalPrice
        list[BuyableID].isDeal = isInDeal(list[BuyableID]?.sku, store)
        //list[BuyableID].cartTotal += itemUnit.TotalPrice
        //list[BuyableID].rewardPoints += Number(itemUnit.PointsEarned) || 0;
      }
    }
  })

  // BackOrder
  // -----------------
  Object.entries(backorder).forEach(([dataKey, item]) => {
    const itemCase = item.case || false
    const itemUnit = item.unit || false

    const BuyableID = itemCase ? itemCase.BuyableID : itemUnit.BuyableID

    if (products[BuyableID] !== undefined) {
      // Set default if doesn't exist
      list = createIfNotExist(list, BuyableID)

      // Add cart data
      const { qty_per_case, sell_by_case } = list[BuyableID]

      if (itemCase) {
        list[BuyableID]._backorderItemID_Case = itemCase.ID
        list[BuyableID].case_price = itemCase.UnitPrice
        list[BuyableID].caseTotal += itemCase.TotalUnitItems
        list[BuyableID].caseBackOrder += itemCase.TotalUnitItems
        list[BuyableID].backOrderQty += sell_by_case ? itemCase.TotalUnitItems : itemCase.TotalUnitItems * qty_per_case
        list[BuyableID].backOrderTotal += itemCase.CalculatedSubTotalPrice
        //list[BuyableID].backOrderTotal += itemCase.TotalPrice
      }

      if (itemUnit) {
        list[BuyableID]._backorderItemID_Unit = itemUnit.ID
        list[BuyableID].unit_price = itemUnit.UnitPrice
        list[BuyableID].unitTotal += itemUnit.TotalUnitItems
        list[BuyableID].unitBackOrder += itemUnit.TotalUnitItems
        list[BuyableID].backOrderQty += itemUnit.TotalUnitItems
        list[BuyableID].backOrderTotal += itemUnit.CalculatedSubTotalPrice
        //list[BuyableID].backOrderTotal += itemUnit.TotalPrice

        // Reconcile overflown units
        // if(list[BuyableID].unitTotal >= qty_per_case) {
        //     const overflownCase = Math.floor(list[BuyableID].unitTotal / qty_per_case);
        //
        //     list[BuyableID].unitTotal = list[BuyableID].unitTotal % qty_per_case;
        //     list[BuyableID].caseTotal += overflownCase;
        // }
      }
    }
  })

  return list
}

/**
 * Helper function to generate cart / backorder item payload for reducer
 *
 * @param product
 * @param cartID
 * @param bundleType
 * @param quantity
 * @returns {{UnitPrice, BundleType, TotalUnitItems, DiscountTotal, BuyableID, TotalPrice, Title, ID}}
 */
export const generateCartItemPayload = (product, cartID = null, bundleType, quantity) => {
  const individualPrice = bundleType === 'case' ? product.case_price : product.unit_price
  const totalPrice = individualPrice * quantity
  const discountTotal = 0 // TODO
  const { StatusCredits, PointsEarned } = getProductPoints(product, quantity, bundleType)

  return generateCartItem({
    BundleType: bundleType,
    BuyableID: product.id,
    CalculatedSubTotalPrice: individualPrice * quantity,
    DiscountTotal: discountTotal,
    ID: cartID ? cartID : `_dummy-${product.id}`,
    ItemsInCase: product.qty_per_case,
    PointsEarned: PointsEarned,
    StatusCredits: StatusCredits,
    Title: product.title,
    TotalPrice: totalPrice,
    TotalUnitItems: quantity,
    UnitPrice: individualPrice,
    WebCategory: product.webCategory,
    LoyaltyPoints: product.loyaltyPoints,
    isDealNew: true,
  })
}

export const getProductPoints = (product, quantity, bundleType) => {
  const { loyaltyPoints, supplierLoyaltyPoints, bonusLoyaltyPoints, wholesale_unit_per_case } = product
  let normalPoints = 0
  let bonusPoints = 0
  if (bundleType == 'case') {
    normalPoints = quantity * wholesale_unit_per_case * (loyaltyPoints * (supplierLoyaltyPoints || 1))
    bonusPoints = quantity * wholesale_unit_per_case * bonusLoyaltyPoints
  } else {
    normalPoints = quantity * (loyaltyPoints * (supplierLoyaltyPoints || 1))
    bonusPoints = quantity * bonusLoyaltyPoints
  }
  return {
    StatusCredits: normalPoints,
    PointsEarned: normalPoints + bonusPoints,
  }
}

export const getCartTotalPoints = (cart) =>
  Object.values(cart.list).reduce(
    (total, product) =>
      Object.values(product).reduce(
        (productPoints, bundle) => ({
          pointsEarned: productPoints.pointsEarned + bundle.PointsEarned,
          statusCredits: productPoints.statusCredits + bundle.StatusCredits,
        }),
        total
      ),
    { pointsEarned: 0, statusCredits: 0 }
  )

export const getBackorderIdsSupplier = (backorder = {}, suppliers = []) => {
  const bundleIds = new Set()

  for (const orderDetails of Object.values(backorder)) {
    for (const bundleType of Object.values(orderDetails)) {
      if (bundleType.SupplierExternalID && suppliers?.includes(bundleType.SupplierExternalID)) {
        bundleIds.add(bundleType.ID)
      }
    }
  }

  return Array.from(bundleIds)
}

export const getFilterBackorder = (backorder = {}, suppliers = []) => {
  const filteredBackorder = {}

  for (const key in backorder) {
    const item = backorder[key]

    // Check for both 'case' and 'unit', if they exist
    if (item.case && !suppliers?.includes(item.case.SupplierExternalID)) {
      filteredBackorder[key] = { ...filteredBackorder[key], case: item.case }
    }
    if (item.unit && !suppliers?.includes(item.unit.SupplierExternalID)) {
      filteredBackorder[key] = { ...filteredBackorder[key], unit: item.unit }
    }
  }

  return filteredBackorder
}
